import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import QRCode from 'qrcode';
import { api, ApiError, fmt, type EventDto, type OrderDto, type OrderLineDto, type TicketCustomFieldDto } from '../api';
import PrivacyPolicyBody from '../components/PrivacyPolicyBody';
import { PreviewBanner } from '../components/PreviewBanner';
import { usePageTitle } from '../hooks/usePageTitle';
import { usePreviewPassCapture } from '../hooks/usePreviewPass';
import { loadImage, roundRect } from '../lib/canvasCard';

/**
 * Composes a savable/shareable "order slip" image: the payment QR plus enough order detail
 * (code, amount, ticket lines, timestamp) to serve as evidence if the transfer is disputed.
 * This is what lets a buyer move the QR off-screen to their bank app on the same phone.
 * Mirrors the merged checkout card exactly — no bank/account fields, since this only fires
 * when a QR is on screen and the account box is hidden in that case.
 */
async function buildOrderQrCardDataUrl(order: OrderDto, event: EventDto, qrSrc: string): Promise<string> {
  const [qrImg] = await Promise.all([loadImage(qrSrc), document.fonts.ready.catch(() => {})]);

  const scale = 2;
  const width = 420;
  const pad = 32;
  const qrBoxSize = 240;
  const lineLines = order.lines.map((l) => `${l.label} — ${fmt(l.lineTotal)}`);
  const headerH = 66;
  const qrH = qrBoxSize + 24;
  const amountH = 40;
  const footerH = 28 + lineLines.length * 20 + 40;
  const height = pad * 2 + headerH + qrH + amountH + footerH;

  const canvas = document.createElement('canvas');
  canvas.width = width * scale;
  canvas.height = height * scale;
  const ctx = canvas.getContext('2d')!;
  ctx.scale(scale, scale);

  const bg = '#171717';
  const border = '#3a3a3a';
  const fg = '#fafafa';
  const muted = '#a6a6a6';

  roundRect(ctx, 0.5, 0.5, width - 1, height - 1, 16);
  ctx.fillStyle = bg;
  ctx.fill();
  ctx.strokeStyle = border;
  ctx.lineWidth = 1;
  ctx.stroke();

  let y = pad;
  ctx.textAlign = 'center';
  ctx.fillStyle = fg;
  ctx.font = '700 17px Inter, sans-serif';
  ctx.fillText(event.name, width / 2, y + 16);
  y += 26;
  ctx.fillStyle = muted;
  ctx.font = '400 12px "JetBrains Mono", monospace';
  ctx.fillText(`ORDER ${order.code}`, width / 2, y + 10);
  y += headerH - 26;

  const qrBoxX = (width - qrBoxSize) / 2;
  roundRect(ctx, qrBoxX, y, qrBoxSize, qrBoxSize, 12);
  ctx.fillStyle = fg;
  ctx.fill();
  const qrInset = 20;
  ctx.drawImage(qrImg, qrBoxX + qrInset, y + qrInset, qrBoxSize - qrInset * 2, qrBoxSize - qrInset * 2);
  y += qrH;

  ctx.fillStyle = fg;
  ctx.font = '700 20px Inter, sans-serif';
  ctx.fillText(`ยอดที่ต้องโอน ${fmt(order.total)}`, width / 2, y + 16);
  y += amountH;

  ctx.strokeStyle = border;
  ctx.beginPath();
  ctx.moveTo(pad, y);
  ctx.lineTo(width - pad, y);
  ctx.stroke();
  y += 22;

  ctx.font = '600 13px Inter, sans-serif';
  ctx.fillStyle = fg;
  ctx.fillText('รายการบัตร', width / 2, y);
  y += 22;

  ctx.font = '400 12.5px Inter, sans-serif';
  ctx.fillStyle = muted;
  for (const label of lineLines) {
    ctx.fillText(label, width / 2, y);
    y += 20;
  }
  y += 8;

  ctx.font = '400 11px "JetBrains Mono", monospace';
  ctx.fillStyle = muted;
  ctx.fillText(`สร้างเมื่อ ${new Date().toLocaleString('th-TH')} · เก็บไว้เป็นหลักฐานคำสั่งซื้อ`, width / 2, y);

  return canvas.toDataURL('image/png');
}

/**
 * TICK-053: mirrors the server cap (`MAX_SLIP_IMAGE_BYTES`), which is the slip vendors' own image
 * maximum. Checking here is not a security control — the server still returns 413 — it just stops
 * a phone photo from being uploaded over mobile data only to be rejected on arrival.
 */
const MAX_SLIP_BYTES = 4 * 1024 * 1024;

export default function Checkout() {
  const { orderId = '', eventSlug } = useParams<{ orderId: string; eventSlug?: string }>();
  const prefix = eventSlug ? `/e/${eventSlug}` : '';

  usePageTitle(eventSlug, 'Checkout');
  // TICK-085 — a preview link may point straight at a reserved order, not only at the landing page.
  usePreviewPassCapture(eventSlug);
  const navigate = useNavigate();
  const [order, setOrder] = useState<OrderDto | null>(null);
  const [event, setEvent] = useState<EventDto | null>(null);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [slip, setSlip] = useState<File | null>(null);
  const [slipPreview, setSlipPreview] = useState('');
  const [consent, setConsent] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  /** ticketId -> one value-set per ticket unit, so a Car Camper × 3 line can carry 3 plates. */
  const [fieldValues, setFieldValues] = useState<Record<string, Record<string, string>[]>>({});
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [savingQr, setSavingQr] = useState(false);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 640);
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const onChange = () => setIsMobile(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    Promise.all([api.getOrder(orderId, eventSlug), api.getEvent(eventSlug)])
      .then(([o, ev]) => {
        if (o.status !== 'pending_payment') {
          navigate(o.status === 'expired' ? `${prefix}/?expired=1` : `${prefix}/order/${o.code}`, { replace: true });
          return;
        }
        setOrder(o);
        setEvent(ev);
        setSecondsLeft(o.secondsLeft);
      })
      .catch(() => navigate(prefix || '/', { replace: true }));
  }, [orderId, eventSlug, navigate, prefix]);

  useEffect(() => {
    if (secondsLeft === null) return;
    if (secondsLeft <= 0) {
      navigate(`${prefix}/?expired=1`, { replace: true });
      return;
    }
    const t = setTimeout(() => setSecondsLeft((s) => (s === null ? null : s - 1)), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft, navigate, prefix]);

  useEffect(() => {
    if (!order?.promptpayPayload) {
      setQrDataUrl('');
      return;
    }
    let cancelled = false;
    QRCode.toDataURL(order.promptpayPayload, { margin: 1, width: 220 })
      .then((url) => { if (!cancelled) setQrDataUrl(url); })
      .catch(() => { if (!cancelled) setQrDataUrl(''); });
    return () => { cancelled = true; };
  }, [order?.promptpayPayload]);

  useEffect(() => {
    if (!slip) {
      setSlipPreview('');
      return;
    }
    const url = URL.createObjectURL(slip);
    setSlipPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [slip]);

  const pickSlip = (file: File | null) => {
    // Clearing the input lets the same file be re-picked after a rejection — without this, a second
    // pick of the identical file fires no change event and the form looks stuck.
    if (fileRef.current) fileRef.current.value = '';
    if (file && file.size > MAX_SLIP_BYTES) {
      setSlip(null);
      setError('ไฟล์สลิปใหญ่เกิน 4 MB กรุณาย่อขนาดรูปแล้วลองใหม่ / Slip image must be 4 MB or smaller');
      return;
    }
    setError('');
    setSlip(file);
  };

  const customFieldLines = useMemo(() => {
    if (!order || !event) return [] as { line: OrderLineDto; fields: TicketCustomFieldDto[] }[];
    const ticketsById = new Map(event.tickets.map((t) => [t.id, t]));
    return order.lines
      .map((line) => ({ line, fields: ticketsById.get(line.ticketId)?.customFields ?? [] }))
      .filter((x) => x.fields.length > 0);
  }, [order, event]);

  const missingRequiredField = useMemo(() => {
    for (const { line, fields } of customFieldLines) {
      for (let unit = 0; unit < line.qty; unit++) {
        for (const f of fields) {
          if (f.required && !(fieldValues[line.ticketId]?.[unit]?.[f.key] ?? '').trim()) return { line, unit, field: f };
        }
      }
    }
    return null;
  }, [customFieldLines, fieldValues]);

  const setFieldValue = (ticketId: string, unit: number, key: string, value: string) =>
    setFieldValues((v) => {
      const sets = [...(v[ticketId] ?? [])];
      sets[unit] = { ...sets[unit], [key]: value };
      return { ...v, [ticketId]: sets };
    });

  const formOk = useMemo(
    () => name.trim().length >= 2 && phone.trim().length >= 9 && !!slip && consent && !missingRequiredField,
    [name, phone, slip, consent, missingRequiredField],
  );

  if (!order || !event || secondsLeft === null) return <div className="page" />;

  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, '0');
  const ss = String(secondsLeft % 60).padStart(2, '0');

  // Prefer the account that actually produced this order's QR — after a fallback it may not be
  // the event's default one, and showing the default's number here would misdirect the transfer.
  const payTo = order.paymentAccount
    ? {
        bankName: order.paymentAccount.bankName ?? '',
        accountName: order.paymentAccount.displayName,
        accountNo: order.paymentAccount.identifier,
        qrImageUrl: order.paymentAccount.qrImageUrl,
      }
    : {
        bankName: event.bank?.bankName ?? '',
        accountName: event.bank?.accountName ?? '',
        accountNo: event.bank?.accountNo ?? '',
        qrImageUrl: event.bank?.qrImageUrl ?? null,
      };

  const copyAcct = async () => {
    try {
      await navigator.clipboard.writeText(payTo.accountNo.replace(/-/g, ''));
    } catch {
      /* clipboard unavailable — non-fatal */
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const qrSrc = qrDataUrl || payTo.qrImageUrl || '';

  const saveOrderQr = async () => {
    if (!qrSrc || savingQr) return;
    setSavingQr(true);
    try {
      const dataUrl = await buildOrderQrCardDataUrl(order, event, qrSrc);
      const filename = `jamtick-order-${order.code}.png`;
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], filename, { type: 'image/png' });
      if (navigator.canShare?.({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: `คำสั่งซื้อ ${order.code}`, text: `ยอดโอน ${fmt(order.total)}` });
          return;
        } catch (err) {
          if ((err as DOMException)?.name === 'AbortError') return;
          // share failed for a reason other than user-cancel — fall through to a direct download
        }
      }
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = filename;
      a.click();
    } finally {
      setSavingQr(false);
    }
  };

  const cancel = async () => {
    try {
      await api.cancelOrder(order.id, eventSlug);
    } catch {
      /* already expired/cancelled server-side */
    }
    navigate(prefix || '/');
  };

  const confirm = async () => {
    if (!formOk || submitting || !slip) return;
    setSubmitting(true);
    setError('');
    const form = new FormData();
    form.set('name', name.trim());
    form.set('phone', phone.trim());
    form.set('email', email.trim());
    form.set('slip', slip);
    form.set('consent', String(consent));
    if (customFieldLines.length) form.set('customFieldValues', JSON.stringify(fieldValues));
    try {
      const updated = await api.submitOrder(order.id, form, eventSlug);
      navigate(`${prefix}/order/${updated.code}`, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 410) {
        navigate(`${prefix}/?expired=1`, { replace: true });
        return;
      }
      setError(err instanceof ApiError ? err.message : 'ส่งข้อมูลไม่สำเร็จ กรุณาลองใหม่');
      setSubmitting(false);
    }
  };

  return (
    <div className="page">
      <PreviewBanner eventSlug={eventSlug} />
      <header className="site-header shell">
        <div className="logo">{event.name}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>ล็อกบัตรไว้ให้อีก</span>
          <span className={`timer-box ${secondsLeft < 60 ? 'pulse-fast' : ''}`}>{mm}:{ss}</span>
        </div>
      </header>

      <div className="checkout-wrap">
        <button className="btn-link" style={{ marginBottom: 24 }} onClick={cancel}>
          ← ยกเลิกและกลับไปเลือกบัตร
        </button>
        <h1>CHECKOUT</h1>

        <div className="card qr-order-card">
          <div className="card-step">1 · คำสั่งซื้อ</div>
          <div className="qr-order-title">{event.name}</div>
          <div className="qr-order-code">ORDER {order.code}</div>

          {qrDataUrl ? (
            <div className="promptpay-box">
              <img src={qrDataUrl} alt="PromptPay QR" width={240} height={240} />
              <div className="s" style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 8 }}>
                สแกนด้วยแอปธนาคาร — ยอดถูกกำหนดในคิวอาร์แล้ว
              </div>
            </div>
          ) : payTo.qrImageUrl ? (
            <div className="promptpay-box">
              <img src={payTo.qrImageUrl} alt="QR รับเงิน" width={240} height={240} style={{ objectFit: 'contain' }} />
              <div className="s" style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 8 }}>
                สแกนด้วยแอปธนาคาร แล้วกรอกยอด <b>{fmt(order.total)}</b> เอง
              </div>
            </div>
          ) : null}

          <div className="qr-order-amount">ยอดที่ต้องโอน {fmt(order.total)}</div>

          {qrSrc && (
            <div className="save-qr-row">
              <button className="btn-ghost" onClick={saveOrderQr} disabled={savingQr}>
                {savingQr ? 'กำลังสร้างรูป…' : 'บันทึก/แชร์ QR คำสั่งซื้อ'}
              </button>
              {isMobile && (
                <div className="save-qr-hint">
                  จ่ายจากมือถือเครื่องนี้เลยใช่ไหม? บันทึกรูปนี้ไว้ก่อน แล้วเปิดแอปธนาคาร → สแกน QR เพื่อจ่ายเงิน → เลือก "เลือกรูปจากคลังภาพ"
                </div>
              )}
            </div>
          )}
          {!qrSrc && payTo.accountNo && (
            <div className="bank-box">
              <div>
                <div className="bank-name">{[payTo.bankName, payTo.accountName].filter(Boolean).join(' · ')}</div>
                <div className="bank-acct">{payTo.accountNo}</div>
              </div>
              <button className="btn-ghost" onClick={copyAcct}>
                {copied ? 'คัดลอกแล้ว ✓' : 'คัดลอกเลขบัญชี'}
              </button>
            </div>
          )}

          <div className="qr-order-divider" />
          <div className="qr-order-lines-label">รายการบัตร</div>
          {order.lines.map((l) => (
            <div className="qr-order-line" key={l.ticketId}>{l.label} — {fmt(l.lineTotal)}</div>
          ))}
          {order.couponDiscount > 0 && (
            <div className="qr-order-line">โค้ด {order.couponCode} — −{fmt(order.couponDiscount)}</div>
          )}

          <div className="transfer-note">
            โอนยอด <b>{fmt(order.total)}</b> ภายในเวลาที่กำหนด แล้วแนบสลิปด้านล่าง — ทีมงานตรวจสอบสลิปทุกรายการ
          </div>
        </div>

        <div className="card">
          <div className="card-step">2 · ข้อมูลผู้ซื้อ</div>
          <div className="form-grid">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="ชื่อ-นามสกุล *" autoComplete="name" />
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="เบอร์โทรศัพท์ *" autoComplete="tel" inputMode="tel" />
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="อีเมล (สำหรับส่งบัตร)" autoComplete="email" inputMode="email" />
          </div>

          {customFieldLines.map(({ line, fields }) => {
            const ticketName = line.label.replace(/\s*×\s*\d+$/, '');
            return Array.from({ length: line.qty }, (_, unit) => (
              <div key={`${line.ticketId}-${unit}`} className="checkout-custom-fields">
                <div className="checkout-custom-fields-ticket">
                  {ticketName}{line.qty > 1 ? ` — ใบที่ ${unit + 1} จาก ${line.qty}` : ''}
                </div>
                <div className="checkout-custom-fields-row">
                  {fields.map((f) => (
                    <label key={f.key} className="ticket-custom-field">
                      <span>{f.labelTh}{f.required && <span className="req"> *</span>}</span>
                      {f.type === 'select' ? (
                        <select
                          value={fieldValues[line.ticketId]?.[unit]?.[f.key] ?? ''}
                          onChange={(e) => setFieldValue(line.ticketId, unit, f.key, e.target.value)}
                        >
                          <option value="" disabled>เลือก...</option>
                          {(f.options ?? []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                      ) : (
                        <input
                          type={f.type === 'number' ? 'number' : 'text'}
                          value={fieldValues[line.ticketId]?.[unit]?.[f.key] ?? ''}
                          onChange={(e) => setFieldValue(line.ticketId, unit, f.key, e.target.value)}
                          placeholder={f.labelEn}
                        />
                      )}
                    </label>
                  ))}
                </div>
              </div>
            ));
          })}

          <label className="consent-row">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
            <span>
              ยินยอมให้เก็บและใช้ข้อมูลส่วนบุคคลเพื่อดำเนินการคำสั่งซื้อนี้ ตาม{' '}
              {/*
                A modal, not a link. `target="_blank"` is a suggestion the LINE and Instagram in-app
                browsers do not honour — they navigate in place — so reading the policy meant leaving
                a half-filled checkout, and coming back lost the typed contact details and the
                attached slip. Consent should never cost the form it is being given on.
              */}
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); setShowPrivacy(true); }}
                style={{
                  background: 'none', border: 'none', padding: 0, font: 'inherit',
                  color: 'inherit', textDecoration: 'underline', cursor: 'pointer',
                }}
              >
                นโยบายความเป็นส่วนตัว
              </button>
            </span>
          </label>
        </div>

        <div className="card" style={{ marginBottom: 28 }}>
          <div className="card-step">3 · แนบสลิปโอนเงิน</div>
          {!slip ? (
            <label className="slip-drop">
              <span className="t">อัปโหลดภาพสลิป</span>
              <span className="s">JPG / PNG / WEBP · สูงสุด 4 MB — แตะเพื่อเลือกไฟล์</span>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic"
                style={{ display: 'none' }}
                onChange={(e) => pickSlip(e.target.files?.[0] ?? null)}
              />
            </label>
          ) : (
            <div className="slip-preview">
              <img src={slipPreview} alt="สลิปโอนเงิน" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <span style={{ fontSize: 13.5 }}>แนบสลิปแล้ว ✓</span>
                <button className="btn-ghost" style={{ alignSelf: 'flex-start' }} onClick={() => setSlip(null)}>
                  เปลี่ยนรูป
                </button>
              </div>
            </div>
          )}
        </div>

        {formOk ? (
          <button className="btn-primary confirm-btn" onClick={confirm} disabled={submitting}>
            {submitting ? 'กำลังส่งข้อมูล…' : 'ยืนยันการชำระเงิน / CONFIRM PAYMENT'}
          </button>
        ) : (
          <button className="btn-primary confirm-btn disabled" disabled>
            กรอกชื่อ เบอร์โทร แนบสลิป และติ๊กยินยอมก่อนยืนยัน
          </button>
        )}
        {error && <div className="form-error">{error}</div>}
      </div>

      {showPrivacy && (
        <div className="sa-modal-overlay" onClick={() => setShowPrivacy(false)}>
          <div
            className="sa-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="privacy-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sa-modal-head">
              <div className="title" id="privacy-modal-title">นโยบายความเป็นส่วนตัว / PRIVACY POLICY</div>
              <button className="btn-ghost" onClick={() => setShowPrivacy(false)} aria-label="ปิด">ปิด</button>
            </div>
            <div className="sa-modal-body" style={{ lineHeight: 1.8, fontSize: 14 }}>
              <PrivacyPolicyBody />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
